import config from "@nusrif/config";
import { formatCurrency } from "@nusrif/utils";
import { ApiCartLine } from "@nusrif/types";

interface AppliedCoupon {
  code: string;
  discount: number;
  type: "percentage" | "fixed";
  description: string;
}

interface CartSummary {
  itemCount: number;
  subtotal: number;
  deliveryFee: number;
}

interface OrderSummaryProps {
  cartLines: ApiCartLine[];
  summary: CartSummary;
  appliedCoupon?: AppliedCoupon | null;
  finalTotal: number;
}

export const OrderSummary = ({
  cartLines,
  summary,
  appliedCoupon,
  finalTotal,
}: OrderSummaryProps) => {
  const calculateCouponDiscount = (): number => {
    if (!appliedCoupon) return 0;
    return appliedCoupon.type === "percentage"
      ? (summary.subtotal * appliedCoupon.discount) / 100
      : appliedCoupon.discount;
  };

  const couponDiscount = calculateCouponDiscount();

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
      <h3 className="text-lg font-semibold text-black">Order Summary</h3>
      {/* Items List */}
      <div className="space-y-3">
        {cartLines.map((line) => (
          <div key={line.id} className="flex items-center gap-3 py-2">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-xs font-medium text-gray-600">
                {line.quantity}x
              </span>
            </div>
            <div className="flex-1">
              <p className="font-medium text-black text-sm">{line.productVariant.product.name}</p>
              <p className="text-xs text-gray-600">{line.productVariant.name}</p>
            </div>
            <p className="font-medium text-black">
              {formatCurrency(line.linePriceWithTax, config.region.currencyCode)}
            </p>
          </div>
        ))}
      </div>
      {/* Price Breakdown */}
      <div className="space-y-3 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">
            Subtotal ({summary.itemCount} item
            {summary.itemCount !== 1 ? "s" : ""})
          </span>
          <span className="font-medium text-black">
            {formatCurrency(summary.subtotal, config.region.currencyCode)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Delivery Fee</span>
          <span
            className={`font-medium ${
              summary.deliveryFee === 0 ? "text-green-600" : "text-black"
            }`}
          >
            {summary.deliveryFee === 0
              ? "FREE"
              : formatCurrency(summary.deliveryFee, config.region.currencyCode)}
          </span>
        </div>
        {appliedCoupon && couponDiscount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-green-600">
              Coupon Discount ({appliedCoupon.code})
            </span>
            <span className="font-medium text-green-600">
              -₹{couponDiscount.toFixed(2)}
            </span>
          </div>
        )}
        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-black text-lg">
              Total Amount
            </span>
            <span className="font-bold text-[#1e4aa8] text-xl">
              {formatCurrency(finalTotal, config.region.currencyCode)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
